import { useState, useEffect, useRef } from 'react';
import { Calendar, Settings, Mail, Clock } from 'lucide-react';
import { format } from 'date-fns';
import {
  PayrollPeriodSettings,
  getPayrollPeriodSettings,
  savePayrollPeriodSettings,
  savePayrollHoursFormat,
  loadPayrollHoursFormat,
  getUpcomingPeriods,
  applyOvertimeToUnpaidHistory
} from '../../lib/payrollPeriodSettings';
import { clearPayrollHoursLabelCache } from '../../lib/payrollHoursLabel';
import toast from 'react-hot-toast';

export const PAYROLL_PERIOD_SETTINGS_CHANGED = 'payrollPeriodSettingsChanged';

interface PayrollPeriodSettingsFormProps {
  businessId: string;
}

function settingsFromRecord(current: PayrollPeriodSettings | null) {
  return {
    periodType: current?.period_type ?? 'biweekly' as const,
    startDate: current?.start_date || format(new Date(), 'yyyy-MM-dd'),
    period1StartDay: current?.period_1_start_day ?? 26,
    period1EndDay: current?.period_1_end_day ?? 10,
    period2StartDay: current?.period_2_start_day ?? 11,
    period2EndDay: current?.period_2_end_day ?? 25,
    accountantEmail: current?.accountant_email?.trim() ?? '',
    overtimeEnabled: current?.overtime_enabled ?? false,
    overtimeScope: (current?.overtime_enabled && !current?.overtime_effective_from
      ? 'history'
      : 'new') as 'new' | 'history',
    workWeekStartDay: current?.work_week_start_day ?? 0,
    dailyOtThreshold: current?.daily_ot_threshold_hours ?? 8,
    dailyDtThreshold: current?.daily_dt_threshold_hours ?? 12,
    weeklyOtThreshold: current?.weekly_ot_threshold_hours ?? 40,
    otMultiplier: current?.ot_multiplier ?? 1.5,
    dtMultiplier: current?.dt_multiplier ?? 2,
    decimalHours: current?.hours_format === 'decimal',
  };
}

export default function PayrollPeriodSettingsForm({ businessId }: PayrollPeriodSettingsFormProps) {
  const [loaded, setLoaded] = useState<PayrollPeriodSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [periodType, setPeriodType] = useState<'weekly' | 'biweekly' | 'semi_monthly' | 'monthly' | 'custom_semi_monthly'>('biweekly');
  const [startDate, setStartDate] = useState<string>(format(new Date(), 'yyyy-MM-dd'));
  const [period1StartDay, setPeriod1StartDay] = useState<number>(26);
  const [period1EndDay, setPeriod1EndDay] = useState<number>(10);
  const [period2StartDay, setPeriod2StartDay] = useState<number>(11);
  const [period2EndDay, setPeriod2EndDay] = useState<number>(25);
  const [accountantEmail, setAccountantEmail] = useState('');
  const [overtimeEnabled, setOvertimeEnabled] = useState(false);
  const [overtimeScope, setOvertimeScope] = useState<'new' | 'history'>('new');
  const [workWeekStartDay, setWorkWeekStartDay] = useState(0);
  const [dailyOtThreshold, setDailyOtThreshold] = useState(8);
  const [dailyDtThreshold, setDailyDtThreshold] = useState(12);
  const [weeklyOtThreshold, setWeeklyOtThreshold] = useState(40);
  const [otMultiplier, setOtMultiplier] = useState(1.5);
  const [dtMultiplier, setDtMultiplier] = useState(2);
  const [decimalHours, setDecimalHours] = useState(false);
  const [savingHoursFormat, setSavingHoursFormat] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const hoursFormatRequest = useRef(0);

  const applyLoaded = (current: PayrollPeriodSettings | null) => {
    const next = settingsFromRecord(current);
    setPeriodType(next.periodType);
    setStartDate(next.startDate);
    setPeriod1StartDay(next.period1StartDay);
    setPeriod1EndDay(next.period1EndDay);
    setPeriod2StartDay(next.period2StartDay);
    setPeriod2EndDay(next.period2EndDay);
    setAccountantEmail(next.accountantEmail);
    setOvertimeEnabled(next.overtimeEnabled);
    setOvertimeScope(next.overtimeScope);
    setWorkWeekStartDay(next.workWeekStartDay);
    setDailyOtThreshold(next.dailyOtThreshold);
    setDailyDtThreshold(next.dailyDtThreshold);
    setWeeklyOtThreshold(next.weeklyOtThreshold);
    setOtMultiplier(next.otMultiplier);
    setDtMultiplier(next.dtMultiplier);
    setDecimalHours(next.decimalHours);
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getPayrollPeriodSettings(businessId)
      .then(async (settings) => {
        if (cancelled) return;
        setLoaded(settings);
        applyLoaded(settings);
        const hoursFormat = await loadPayrollHoursFormat(businessId, settings);
        if (!cancelled) setDecimalHours(hoursFormat === 'decimal');
      })
      .catch((error) => {
        console.error('Error loading payroll period settings:', error);
        if (!cancelled) toast.error('Failed to load payroll settings');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [businessId]);

  const isValidEmail = (email: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleDecimalHours = async () => {
    if (savingHoursFormat || isSaving) return;
    const next = !decimalHours;
    const previous = decimalHours;
    setDecimalHours(next);
    const requestId = hoursFormatRequest.current + 1;
    hoursFormatRequest.current = requestId;
    setSavingHoursFormat(true);
    try {
      const saved = await savePayrollHoursFormat(
        businessId,
        next ? 'decimal' : 'hours_minutes',
      );
      if (requestId !== hoursFormatRequest.current) return;
      if (saved) {
        setLoaded((current) => current ? { ...current, ...saved } : saved);
      }
      clearPayrollHoursLabelCache();
      toast.success('Setting saved successfully');
    } catch (error) {
      console.error('Error saving decimal hours:', error);
      if (requestId !== hoursFormatRequest.current) return;
      setDecimalHours(previous);
      toast.error('Failed to save setting');
    } finally {
      if (requestId === hoursFormatRequest.current) setSavingHoursFormat(false);
    }
  };

  const handleSave = async () => {
    const trimmedAccountantEmail = accountantEmail.trim();

    if (trimmedAccountantEmail && !isValidEmail(trimmedAccountantEmail)) {
      toast.error('Please enter a valid accountant email address');
      return;
    }

    try {
      setIsSaving(true);

      const overtimeEffectiveFrom = !overtimeEnabled
        ? loaded?.overtime_effective_from || null
        : overtimeScope === 'history'
          ? null
          : (loaded?.overtime_enabled && loaded.overtime_effective_from)
            ? loaded.overtime_effective_from
            : format(new Date(), 'yyyy-MM-dd');

      const settings: PayrollPeriodSettings = {
        business_id: businessId,
        period_type: periodType,
        accountant_email: trimmedAccountantEmail || null,
        overtime_enabled: overtimeEnabled,
        overtime_effective_from: overtimeEffectiveFrom,
        work_week_start_day: workWeekStartDay,
        daily_ot_threshold_hours: dailyOtThreshold,
        daily_dt_threshold_hours: dailyDtThreshold,
        weekly_ot_threshold_hours: weeklyOtThreshold,
        ot_multiplier: otMultiplier,
        dt_multiplier: dtMultiplier,
        hours_format: decimalHours ? 'decimal' : 'hours_minutes',
        ...(periodType === 'custom_semi_monthly' ? {
          period_1_start_day: period1StartDay,
          period_1_end_day: period1EndDay,
          period_2_start_day: period2StartDay,
          period_2_end_day: period2EndDay
        } : {
          start_date: startDate
        })
      };

      const savedSettings = await savePayrollPeriodSettings(settings);

      if (overtimeEnabled && overtimeScope === 'history') {
        const weeks = await applyOvertimeToUnpaidHistory(businessId);
        toast.success(
          weeks > 0
            ? `Settings saved. Overtime recalculated for ${weeks} past work week${weeks === 1 ? '' : 's'} (unpaid entries only).`
            : 'Settings saved. No past unpaid entries needed recalculating.'
        );
      } else {
        toast.success('Payroll period settings saved successfully');
      }

      setLoaded(savedSettings);
      applyLoaded(savedSettings);
      window.dispatchEvent(new CustomEvent(PAYROLL_PERIOD_SETTINGS_CHANGED, {
        detail: savedSettings
      }));
    } catch (error) {
      console.error('Error saving payroll period settings:', error);
      toast.error('Failed to save payroll period settings');
    } finally {
      setIsSaving(false);
    }
  };

  const previewPeriods = (() => {
    try {
      if (periodType === 'custom_semi_monthly') {
        return getUpcomingPeriods({
          business_id: businessId,
          period_type: periodType,
          period_1_start_day: period1StartDay,
          period_1_end_day: period1EndDay,
          period_2_start_day: period2StartDay,
          period_2_end_day: period2EndDay
        }, 3);
      } else if (periodType && startDate) {
        return getUpcomingPeriods({
          business_id: businessId,
          period_type: periodType,
          start_date: startDate
        }, 3);
      }
      return [];
    } catch (error) {
      console.error('Error calculating preview periods:', error);
      return [];
    }
  })();

  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-6 text-sm text-gray-500">
        Loading pay period settings…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 sm:mb-3 px-1">Pay Period</h3>
        <p className="text-xs text-gray-500 px-1 -mt-1 mb-3">
          Configure your business pay period schedule
        </p>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <label className="block text-sm font-semibold text-gray-900 mb-3">
          <Settings className="h-4 w-4 inline mr-2 text-gray-500" />
          Payroll Frequency
        </label>
        <select
          value={periodType}
          onChange={(e) => setPeriodType(e.target.value as typeof periodType)}
          className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 transition-colors"
        >
          <option value="weekly">Weekly (Every 7 days)</option>
          <option value="biweekly">Biweekly (Every 14 days)</option>
          <option value="semi_monthly">Semi-Monthly (1st-15th, 16th-End)</option>
          <option value="custom_semi_monthly">Custom Semi-Monthly</option>
          <option value="monthly">Monthly</option>
        </select>
        <p className="mt-2 text-xs text-gray-500">
          {periodType === 'weekly' && 'Employees are paid every week'}
          {periodType === 'biweekly' && 'Employees are paid every two weeks'}
          {periodType === 'semi_monthly' && 'Employees are paid twice per month on the 1st-15th and 16th-end'}
          {periodType === 'custom_semi_monthly' && 'Define custom pay periods with specific day ranges (can cross month boundaries)'}
          {periodType === 'monthly' && 'Employees are paid once per month'}
        </p>
      </div>

      {periodType !== 'semi_monthly' && periodType !== 'custom_semi_monthly' && (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <label className="block text-sm font-semibold text-gray-900 mb-3">
            <Calendar className="h-4 w-4 inline mr-2 text-gray-500" />
            {periodType === 'monthly' ? 'Period Start Day' : 'First Period Start Date'}
          </label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 transition-colors"
          />
          <p className="mt-2 text-xs text-gray-500">
            {periodType === 'weekly' && 'The date when your first weekly pay period begins'}
            {periodType === 'biweekly' && 'The date when your first biweekly pay period begins'}
            {periodType === 'monthly' && 'Pay periods will start on this day of each month'}
          </p>
        </div>
      )}

      {periodType === 'custom_semi_monthly' && (
        <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-5">
          <div>
            <h4 className="text-sm font-semibold text-gray-900 mb-3">First Pay Period</h4>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-2">Start Day</label>
                <input
                  type="number"
                  min="1"
                  max="31"
                  value={period1StartDay}
                  onChange={(e) => setPeriod1StartDay(parseInt(e.target.value) || 1)}
                  className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 transition-colors"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-2">End Day</label>
                <input
                  type="number"
                  min="1"
                  max="31"
                  value={period1EndDay}
                  onChange={(e) => setPeriod1EndDay(parseInt(e.target.value) || 1)}
                  className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 transition-colors"
                />
              </div>
            </div>
            <p className="mt-2 text-xs text-gray-500">
              Example: 26-10 (crosses month boundary)
            </p>
          </div>

          <div className="border-t border-gray-100 pt-5">
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Second Pay Period</h4>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-2">Start Day</label>
                <input
                  type="number"
                  min="1"
                  max="31"
                  value={period2StartDay}
                  onChange={(e) => setPeriod2StartDay(parseInt(e.target.value) || 1)}
                  className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 transition-colors"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-2">End Day</label>
                <input
                  type="number"
                  min="1"
                  max="31"
                  value={period2EndDay}
                  onChange={(e) => setPeriod2EndDay(parseInt(e.target.value) || 1)}
                  className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 transition-colors"
                />
              </div>
            </div>
            <p className="mt-2 text-xs text-gray-500">
              Example: 11-25 (within same month)
            </p>
          </div>
        </div>
      )}

      {previewPeriods.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Calendar className="h-4 w-4 text-blue-600" />
            Upcoming Pay Periods
          </h3>
          <div className="space-y-2">
            {previewPeriods.map((period, index) => (
              <div
                key={index}
                className={`flex items-center justify-between p-3 rounded-xl border ${
                  index === 0
                    ? 'bg-blue-50 border-blue-200/60'
                    : 'bg-gray-50 border-gray-200/60'
                }`}
              >
                <span className={`text-xs font-semibold ${
                  index === 0 ? 'text-blue-700' : 'text-gray-600'
                }`}>
                  {index === 0 ? 'Current' : `Period ${index + 1}`}
                </span>
                <span className={`text-sm font-semibold ${
                  index === 0 ? 'text-blue-900' : 'text-gray-900'
                }`}>
                  {format(period.start, 'MMM d')} – {format(period.end, 'MMM d, yyyy')}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <label
          htmlFor="accountant-email"
          className="block text-sm font-semibold text-gray-900 mb-3"
        >
          <Mail className="h-4 w-4 inline mr-2 text-gray-500" />
          Accountant
        </label>
        <input
          id="accountant-email"
          type="email"
          value={accountantEmail}
          onChange={(e) => setAccountantEmail(e.target.value)}
          placeholder="accountant@example.com"
          autoComplete="email"
          className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 transition-colors"
        />
        <p className="mt-2 text-xs text-gray-500">
          Optional. Your accountant&apos;s email for payroll-related notifications and sharing.
        </p>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <label className="text-sm font-semibold text-gray-900">
            <Clock className="h-4 w-4 inline mr-2 text-gray-500" />
            Decimal Hours
          </label>
          <button
            type="button"
            role="switch"
            aria-checked={decimalHours}
            aria-busy={savingHoursFormat}
            disabled={savingHoursFormat || isSaving}
            onClick={() => { void handleDecimalHours(); }}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors duration-200 touch-manipulation disabled:opacity-50 ${
              decimalHours ? 'bg-blue-600' : 'bg-gray-200'
            }`}
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform duration-200 ${
                decimalHours ? 'translate-x-[22px]' : 'translate-x-0.5'
              }`}
            />
          </button>
        </div>
        <p className="mt-2 text-xs text-gray-500">
          Show worked time as decimal hours, such as 8.38. Off shows 8h 23m. Pay stays based on the minutes.
        </p>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <label className="text-sm font-semibold text-gray-900">
            <Clock className="h-4 w-4 inline mr-2 text-gray-500" />
            Overtime Pay
          </label>
          <button
            type="button"
            role="switch"
            aria-checked={overtimeEnabled}
            onClick={() => setOvertimeEnabled(!overtimeEnabled)}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors duration-200 touch-manipulation ${
              overtimeEnabled ? 'bg-blue-600' : 'bg-gray-200'
            }`}
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform duration-200 ${
                overtimeEnabled ? 'translate-x-[22px]' : 'translate-x-0.5'
              }`}
            />
          </button>
        </div>
        <p className="mt-2 text-xs text-gray-500">
          When enabled, hourly shifts from that point forward automatically earn overtime.
          Past entries are never changed.
        </p>

        {overtimeEnabled && (
          <div className="mt-4 space-y-4 border-t border-gray-100 pt-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-2">Apply To</label>
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => setOvertimeScope('new')}
                  className={`w-full flex items-start gap-3 p-3 rounded-xl border text-left transition-colors touch-manipulation ${
                    overtimeScope === 'new'
                      ? 'bg-blue-50 border-blue-300'
                      : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                  }`}
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <span className={`mt-0.5 h-4 w-4 flex-shrink-0 rounded-full border-2 flex items-center justify-center ${
                    overtimeScope === 'new' ? 'border-blue-600' : 'border-gray-300'
                  }`}>
                    {overtimeScope === 'new' && <span className="h-2 w-2 rounded-full bg-blue-600" />}
                  </span>
                  <span>
                    <span className="block text-sm font-semibold text-gray-900">New entries only</span>
                    <span className="block text-xs text-gray-500 mt-0.5">
                      Overtime starts today. Existing entries keep their current pay.
                    </span>
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setOvertimeScope('history')}
                  className={`w-full flex items-start gap-3 p-3 rounded-xl border text-left transition-colors touch-manipulation ${
                    overtimeScope === 'history'
                      ? 'bg-blue-50 border-blue-300'
                      : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                  }`}
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <span className={`mt-0.5 h-4 w-4 flex-shrink-0 rounded-full border-2 flex items-center justify-center ${
                    overtimeScope === 'history' ? 'border-blue-600' : 'border-gray-300'
                  }`}>
                    {overtimeScope === 'history' && <span className="h-2 w-2 rounded-full bg-blue-600" />}
                  </span>
                  <span>
                    <span className="block text-sm font-semibold text-gray-900">Include past entries</span>
                    <span className="block text-xs text-gray-500 mt-0.5">
                      Also recalculates all existing unpaid entries. Entries already paid are never changed.
                    </span>
                  </span>
                </button>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-2">Work Week Starts On</label>
              <select
                value={workWeekStartDay}
                onChange={(e) => setWorkWeekStartDay(parseInt(e.target.value))}
                className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 transition-colors"
              >
                <option value={0}>Sunday</option>
                <option value={1}>Monday</option>
                <option value={2}>Tuesday</option>
                <option value={3}>Wednesday</option>
                <option value={4}>Thursday</option>
                <option value={5}>Friday</option>
                <option value={6}>Saturday</option>
              </select>
              <p className="mt-1.5 text-xs text-gray-500">
                Weekly overtime counts hours within this calendar week (separate from pay periods).
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-2">Daily OT After (hours)</label>
                <input
                  type="number"
                  min="1"
                  max="24"
                  step="0.5"
                  value={dailyOtThreshold}
                  onChange={(e) => setDailyOtThreshold(parseFloat(e.target.value) || 8)}
                  className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 transition-colors"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-2">OT Multiplier</label>
                <input
                  type="number"
                  min="1"
                  max="5"
                  step="0.1"
                  value={otMultiplier}
                  onChange={(e) => setOtMultiplier(parseFloat(e.target.value) || 1.5)}
                  className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 transition-colors"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-2">Double Time After (hours)</label>
                <input
                  type="number"
                  min="1"
                  max="24"
                  step="0.5"
                  value={dailyDtThreshold}
                  onChange={(e) => setDailyDtThreshold(parseFloat(e.target.value) || 12)}
                  className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 transition-colors"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-2">Double Time Multiplier</label>
                <input
                  type="number"
                  min="1"
                  max="5"
                  step="0.1"
                  value={dtMultiplier}
                  onChange={(e) => setDtMultiplier(parseFloat(e.target.value) || 2)}
                  className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 transition-colors"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-2">Weekly OT After (hours)</label>
              <input
                type="number"
                min="1"
                max="168"
                step="0.5"
                value={weeklyOtThreshold}
                onChange={(e) => setWeeklyOtThreshold(parseFloat(e.target.value) || 40)}
                className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 transition-colors"
              />
              <p className="mt-1.5 text-xs text-gray-500">
                Defaults follow BC employment standards: 1.5&times; after {dailyOtThreshold}h/day, {dtMultiplier}&times; after {dailyDtThreshold}h/day, and 1.5&times; after {weeklyOtThreshold}h/week.
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="bg-blue-50 border border-blue-200/60 rounded-xl p-4">
        <p className="text-xs text-blue-800 leading-relaxed">
          Once you save these settings, Current Period and Last Period filters appear in the T-Sheets date range selector.
        </p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 justify-end">
        <button
          type="button"
          onClick={() => applyLoaded(loaded)}
          disabled={isSaving}
          className="px-6 py-3 text-sm font-semibold text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 active:bg-gray-100 active:scale-[0.98] transition-all duration-150 w-full sm:w-auto touch-manipulation"
          style={{ WebkitTapHighlightColor: 'transparent' }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={isSaving}
          className="px-6 py-3 text-sm font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 active:bg-blue-800 active:scale-[0.98] transition-all duration-150 w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
          style={{ WebkitTapHighlightColor: 'transparent' }}
        >
          {isSaving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
}
